/* eslint-disable jsx-a11y/control-has-associated-label,jsx-a11y/interactive-supports-focus,jsx-a11y/click-events-have-key-events */
import styled from "@emotion/styled";
import React, { useState } from "react";
import { compassColors } from "src/utils/styling";
import ConfettiTadaStatic from "src/components/banner/confetti_data_static.svg";
import { useDisableScroll } from "src/hooks/scroll";
import { Modal } from "src/design-system/components/Modal/Modal";
import { Text } from "@components/core/Text";
import { useGlobalEventListener } from "src/hooks/dom";
import { resetFormElement } from "src/design-system/styles/resets";
import {
  getCurrentHolidayPromoConfig,
  getHolidayPromoForcedPlanPrice,
} from "src/utils/holidayPromo";
import { isTaxExclusiveCountry } from "src/utils/userSegment/features";
import i18n from "src/locales";
import {
  PROMO_TYPE,
  PROMO_PRODUCT_TYPE,
  PercentOffPromoConfig,
  FlatAmountOffPromoConfig,
} from "src/utils/holidayPromoConstants";

export const AnimationContainer = styled.div`
  height: 50px;
  width: 50px;
  max-height: 50px;
  max-width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const BannerContainer = styled.div`
  height: 60px;
  background-color: ${compassColors.sky};
  display: flex;
  justify-content: center;
  padding-top: 16px;
  padding-bottom: 16px;
  width: 100%;
`;

export const ColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 600px;
  margin: 0 20px;
  align-items: center;
`;

export const HeadlineContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 10px;
  margin-right: 10px;
`;

export const Headline = styled.span`
  font-family: "Untitled Sans", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`;

export const StaticImg = styled.img`
  height: 40px;
  max-height: 40px;
  width: 40px;
  max-width: 40px;
  margin-top: -6px;
  margin-left: -1px;
`;

const ContentWrapper = styled.div`
  padding: 0 16px;
  overflow: scroll;
  max-height: 300px;
`;

const OfferDetailsButton = styled.button`
  ${resetFormElement};
  text-decoration: underline;
  border: none;
  background: none;
  display: inline;
  padding: 0;
  outline: none;
  box-shadow: none;
`;

function getOfferTermsTranslationKey(
  productTypeTranslationKey: string,
  promoTypeTranslationKey: string,
  section: string
) {
  if (productTypeTranslationKey === PROMO_PRODUCT_TYPE.NOOM_MED) {
    return `holidayPromotion:offerTerms:${productTypeTranslationKey}:${section}:${promoTypeTranslationKey}`;
  }

  return `holidayPromotion:offerTerms:${productTypeTranslationKey}:${section}`;
}

function OfferDetailsModal({ onClose }: { onClose: () => void }) {
  useDisableScroll();

  useGlobalEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  });

  const promoConfig = getCurrentHolidayPromoConfig();

  let promoTypeTranslationKey;
  if (promoConfig.promoType === PROMO_TYPE.MONTHS_OFF) {
    promoTypeTranslationKey = "monthsOff";
  } else if (promoConfig.promoType === PROMO_TYPE.FLAT_AMOUNT_OFF) {
    promoTypeTranslationKey = "flatAmountOff";
  } else {
    promoTypeTranslationKey = "percentageOff";
  }

  const taxTypeTranslationKey = isTaxExclusiveCountry()
    ? "taxExclusive"
    : "taxInclusive";
  const { productTypeTranslationKey } = promoConfig;

  const { percentageOff, flatAmountOff } =
    promoConfig as PercentOffPromoConfig & FlatAmountOffPromoConfig;

  const planPrice = getHolidayPromoForcedPlanPrice();

  return (
    <Modal onClickOutside={onClose} onClose={onClose} includeCloseButton>
      <ContentWrapper>
        <Text i18nKey="holidayPromotion:offerTerms:title" />
        <ul>
          <li>
            <Text
              i18nKey={getOfferTermsTranslationKey(
                productTypeTranslationKey,
                promoTypeTranslationKey,
                "offerTerms"
              )}
            />
          </li>
          <li>
            <Text
              i18nKey={`holidayPromotion:offerTerms:${productTypeTranslationKey}:promoCodeTerms:${promoTypeTranslationKey}:${taxTypeTranslationKey}`}
              values={{
                promoCode: promoConfig.uiPromoCode,
                percentageOff,
                flatAmountOff,
              }}
            />
          </li>
          <li>
            <Text
              i18nKey={getOfferTermsTranslationKey(
                productTypeTranslationKey,
                promoTypeTranslationKey,
                "requirements"
              )}
            />
          </li>
          {productTypeTranslationKey === PROMO_PRODUCT_TYPE.NOOM_MED && (
            <li>
              <Text
                i18nKey={getOfferTermsTranslationKey(
                  productTypeTranslationKey,
                  promoTypeTranslationKey,
                  "limitations"
                )}
              />
            </li>
          )}
        </ul>
        <Text i18nKey="holidayPromotion:subscriptionTerms:title" />
        <ul>
          <li>{i18n.t("holidayPromotion:subscriptionTerms:line1")}</li>
          <li>
            <Text
              i18nKey={`holidayPromotion:subscriptionTerms:${productTypeTranslationKey}:line2:${promoTypeTranslationKey}:${taxTypeTranslationKey}`}
              values={{
                planPrice,
                percentageOff,
                flatAmountOff,
              }}
            />
          </li>
          <li>
            <Text
              i18nKey={`holidayPromotion:subscriptionTerms:${productTypeTranslationKey}:line3:${promoTypeTranslationKey}:${taxTypeTranslationKey}`}
              values={{
                planPrice,
                percentageOff,
                flatAmountOff,
              }}
            />
          </li>
          <li>{i18n.t("holidayPromotion:subscriptionTerms:line4")}</li>
          <li>{i18n.t("holidayPromotion:subscriptionTerms:line5")}</li>
        </ul>
      </ContentWrapper>
    </Modal>
  );
}

export function HolidayPromoBanner() {
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (event) => {
    event.preventDefault();
    setModalOpen(true);
  };
  const promoConfig = getCurrentHolidayPromoConfig();

  return (
    <BannerContainer>
      {modalOpen && <OfferDetailsModal onClose={() => setModalOpen(false)} />}

      <ColumnContainer>
        <AnimationContainer>
          <StaticImg src={ConfettiTadaStatic} />
        </AnimationContainer>
        <HeadlineContainer>
          <Headline>
            <Text
              i18nKey={`holidayPromotion:${promoConfig.translationKey}:headline`}
              components={{
                ModalOpenLink: (
                  <OfferDetailsButton role="button" onClick={openModal} />
                ),
              }}
              values={{
                promoCode: promoConfig.uiPromoCode,
              }}
            />
          </Headline>
        </HeadlineContainer>
      </ColumnContainer>
    </BannerContainer>
  );
}
